import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatTabChangeEvent } from 'node_modules/@angular/material/tabs';

import { Cluster } from 'src/app/models/cluster.model';
import { CurrentPortfolio } from 'src/app/models/current-portfolio.model';
import { CurrentStrategyTarget } from "src/app/models/current-strategy-target.model";
import { InvestableParameters } from 'src/app/models/investable-parameters.model';

import { ClustersService } from 'src/app/services/clusters.service';
import { InvestableService } from 'src/app/services/investable.service';
import { NotificationsService } from 'src/app/services/notifications.service';

@Component({
  selector: 'app-investable-optimization',
  templateUrl: './investable-optimization.component.html',
  styleUrls: ['./investable-optimization.component.scss']
})
export class InvestableOptimizationComponent implements OnInit, OnDestroy {

  @Input() isComputing!: boolean;
  @Output() isComputingChange = new EventEmitter<boolean>();
  @Input() optimizeOk!: boolean;
  @Output() optimizeOkChange = new EventEmitter<boolean>();
  @Input() improveOk!: boolean;
  @Output() improveOkChange = new EventEmitter<boolean>();

  currentCluster: Cluster;
  currentClusterSubscription: Subscription;

  currentOriginal: CurrentPortfolio;
  currentOriginalSubscription: Subscription;
  currentStrategy: CurrentStrategyTarget;
  currentStrategySubscription: Subscription;
  customInvestable: CurrentPortfolio;
  customInvestableSubscription: Subscription;

  currentInvestable: CurrentPortfolio;
  currentInvestableSubscription: Subscription;

  selectedPage2: string = 'weights';

  hasOriginal: boolean = false;
  hasStrategy: boolean = false;
  hasInvestable: boolean = false;

  investableParameters: InvestableParameters;
  investableParametersSubscription: Subscription;

  selectedOptimizationTab: string = 'perfRisk';
  hasImprove: boolean = false;
  improveYesNo: boolean = false;

  panelOpenState2 = true;

  constructor(private clustersService: ClustersService,
              private investableService: InvestableService,
              private notificationsService: NotificationsService,) { }

  ngOnInit(): void {
    this.subscribeToInvestableParameters();
    this.subscribeToCurrentCluster();
    this.subscribeToCurrentOriginal();
    this.subscribeToCurrentStrategy();
    this.subscribeToCurrentInvestable();
    this.subscribeToCustomInvestable();
  }

  ngOnDestroy() {
    if (this.currentClusterSubscription != undefined) { this.currentClusterSubscription.unsubscribe(); }
    if (this.currentOriginalSubscription != undefined) { this.currentOriginalSubscription.unsubscribe(); }
    if (this.currentStrategySubscription != undefined) { this.currentStrategySubscription.unsubscribe(); }
    if (this.currentInvestableSubscription != undefined) { this.currentInvestableSubscription.unsubscribe(); }
    if (this.investableParametersSubscription != undefined) { this.investableParametersSubscription.unsubscribe(); }
    if (this.customInvestableSubscription != undefined) { this.customInvestableSubscription.unsubscribe(); }
  }
            
  subscribeToCurrentCluster() {
    this.currentClusterSubscription = this.clustersService.currentClusterSubject.subscribe(
      (cluster: Cluster) => {
        if (cluster!=undefined) {
          this.currentCluster = cluster;
        }
      }
    );
    this.clustersService.emitCurrentCluster();
  }

  subscribeToInvestableParameters() {
    this.investableParametersSubscription = this.investableService.investableParametersSubject.subscribe(
      (investableParameters: InvestableParameters) => {
        this.investableParameters = investableParameters;
      }
    );
    this.investableService.emitInvestableParameters();
  }

  subscribeToCurrentOriginal() {
    this.currentOriginalSubscription = this.clustersService.currentOriginalSubject.subscribe(
      (original_portfolio: CurrentPortfolio) => {
        this.currentOriginal = original_portfolio;
        this.hasOriginal = (original_portfolio!=undefined);
      }
    );
    this.clustersService.emitCurrentOriginal();
  }

  subscribeToCurrentInvestable() {
    this.currentInvestableSubscription = this.clustersService.currentInvestableSubject.subscribe(
      (investable_portfolio: CurrentPortfolio) => {
        this.currentInvestable = investable_portfolio;
        this.hasInvestable = (investable_portfolio!=undefined);
      }
    );
    this.clustersService.emitCurrentInvestable();
  }

  subscribeToCustomInvestable() {
    this.customInvestableSubscription = this.investableService.customInvestableSubject.subscribe(
      (investable_portfolio: CurrentPortfolio) => {
        this.customInvestable = investable_portfolio;
        this.currentInvestable = investable_portfolio;
        if (investable_portfolio) {
          this.investableParameters.target_volatility = investable_portfolio.parameters.target_volatility;
          this.investableParameters.min_volatility = investable_portfolio.parameters.min_volatility;
          this.investableParameters.max_volatility = investable_portfolio.parameters.max_volatility;
          this.investableParameters.target_tracking_error = investable_portfolio.parameters.target_tracking_error;
          this.investableParameters.min_tracking_error = investable_portfolio.parameters.min_tracking_error;
          this.investableParameters.max_tracking_error = investable_portfolio.parameters.max_tracking_error;
          this.investableParameters.checkConstraints();
        }
        this.hasInvestable = (investable_portfolio!=undefined);
      }
    );
    this.investableService.emitCustomInvestable();
  }

  subscribeToCurrentStrategy() {
    this.currentStrategySubscription = this.clustersService.currentStrategySubject.subscribe(
      (strategy: CurrentStrategyTarget) => {
        this.currentStrategy = strategy;
        this.hasStrategy = (strategy!=undefined);
      }
    );
    this.clustersService.emitCurrentStrategy();
  }

  async onOptimize() {
    if (this.investableParameters.completeness_products.length<2 || this.isComputing) {
      return;
    }
    this.notificationsService.runSpinner();
    this.setOptimizeOk(false);
    this.setImproveOk(false);
    this.setIsComputing(true);
    this.panelOpenState2 = false;

    try {
      await this.investableService.computeCustomInvestable(this.investableParameters, this.currentCluster);
      this.notificationsService.notify('computation-success');
      this.investableParameters.resetImproveConstraints();
      this.setOptimizeOk(true);
    }
    catch(e) {
      this.notificationsService.notify('computation-failed');
      this.setOptimizeOk(false);
    }

    this.setIsComputing(false);
    this.notificationsService.stopSpinner();
  }

  async onImprove() {
    if (this.isComputing) {
      return;
    }
    this.notificationsService.runSpinner();
    this.setImproveOk(false);
    this.setIsComputing(true);

    try {
      await this.investableService.computeCustomInvestable(this.investableParameters, this.currentCluster);
      this.notificationsService.notify('computation-success');
      this.setImproveOk(true);
    }
    catch(e) {
      this.notificationsService.notify('computation-failed');
      this.setImproveOk(false);
    }

    this.setIsComputing(false);
    this.notificationsService.stopSpinner();
  }

  formatLabel(value: number) {
    if (value < 0.1) {
      return Math.round(10000*value)/100;
    } else {
      return Math.round(1000*value)/10;
    }
  }

  onComponentsTabChange(event: MatTabChangeEvent) {
    switch (event.tab.textLabel) {
      case 'Performance & Risk':
        this.selectedOptimizationTab = 'perfRisk';
        break;
      case 'Exposure':
        this.selectedOptimizationTab = 'exposure';
        break;
      case 'Diversification':
        this.selectedOptimizationTab = 'diversification';
        break;
    }
  }

  onImproveCheck(constraint_type: string) {
    switch (constraint_type) {
      case 'improve_solution':
        if (!this.improveYesNo) {
          this.investableParameters.has_best_fit = false;
          this.investableParameters.has_remove_weight_constraints = false;
          this.investableParameters.has_volatility_constraints = false;
          this.investableParameters.has_tracking_error_constraints = false;
        }
        break;
      case 'remove_weight_constraints':
        if (!this.investableParameters.has_remove_weight_constraints) {
          this.investableParameters.has_volatility_constraints = false;
          this.investableParameters.has_tracking_error_constraints = false;
        }
        break;
      case 'volatility_constraints':
        if (this.investableParameters.has_volatility_constraints) {
          this.investableParameters.has_tracking_error_constraints = false;
          this.investableParameters.has_remove_weight_constraints = true;
        }
        break;
      case 'tracking_error_constraints':
        if (this.investableParameters.has_tracking_error_constraints) {
          this.investableParameters.has_volatility_constraints = false;
          this.investableParameters.has_remove_weight_constraints = true;
        }
        break;
    }
    if (this.investableParameters.has_best_fit ||
        this.investableParameters.has_remove_weight_constraints ||
        this.investableParameters.has_volatility_constraints ||
        this.investableParameters.has_tracking_error_constraints) {
      this.hasImprove = true;
    } else {
      this.hasImprove = false;
    }
  }

  setIsComputing(flag: boolean) {
    if (this.isComputing != flag) {
      this.isComputing = flag;
      this.isComputingChange.emit(flag);
    }
  }

  setOptimizeOk(flag: boolean) {
    if (this.optimizeOk != flag) {
      this.optimizeOk = flag;
      this.optimizeOkChange.emit(flag);
    }
  }

  setImproveOk(flag: boolean) {
    if (this.improveOk != flag) {
      this.improveOk = flag;
      this.improveOkChange.emit(flag);
    }
  }

}
